import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {WB_LoaderSourceRow} from "../PA.Core/PAdbSchemaRows";
import {IwbLoaderSource} from "../PA.Core/PAdbSchemaRowsIF";
import {esWB_Loader_Source} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {LoaderSourceBOValidation} from "./LoaderSourceBO.validation";
import {PAEnvironment} from "../PA.Core/PAEnvironment";

export class LoaderSourceBO
    implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_LoaderSource: WB_LoaderSourceRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Loader Source'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_LoaderSource = null;
    }

    get WB_LoaderSource(): WB_LoaderSourceRow {
        return this.drWB_LoaderSource;
    }

    get Schema(): string {

        return "Schema"
    }
    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>

    LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Loader Source");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject)=>{
            self.LoadOptions = options || {};
            if("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null){
                let sError = "Unable to determine dbContext for new Loader Source";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Loader_Source)
                .then((newLoaderSourceRow: WB_LoaderSourceRow)=>{
                    newLoaderSourceRow.setDefaults();
                    self.drWB_LoaderSource = newLoaderSourceRow;
                    //add this to all the BOs
                    self.dbContext.add(esWB_Loader_Source, newLoaderSourceRow);
                    self.Identity.setAll(self.WB_LoaderSource.cif.WB_LOADERSOURCE_ID, self.WB_LoaderSource.cif.LOADERSOURCE_ID, self.WB_LoaderSource.cif.LOADERSOURCE_TYPE);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_LoaderSource, "WB_LOADERSOURCE_ID", "LOADERSOURCE_ID", "LOADERSOURCE_TYPE");

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err)=>{
                    self.LoadProgressProvider.exception(err);
                    reject(err)
                })

        });
        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: any): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject)=>{

            if(self.bIsLoaded) {
                let sError = "Loader Source already loaded";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if(self.dbContext == null)
                self.dbContext = new PAdbContext();

            if(this.dbContext == null){
                let sError = "Unable to determine dbContext for new Loader Source";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Added for record resolver
            if("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = LoaderSourceBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = LoaderSourceBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    let aLoaderSourceRow: any = recordResolver.get(esWB_Loader_Source, anId);

                    if(_.isEmpty(aLoaderSourceRow)){
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_LoaderSource = aLoaderSourceRow;
                    self.Identity.setAll(self.WB_LoaderSource.cif.WB_LOADERSOURCE_ID, self.WB_LoaderSource.cif.LOADERSOURCE_ID, self.WB_LoaderSource.cif.LOADERSOURCE_TYPE);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_LoaderSource, "WB_LOADERSOURCE_ID", "LOADERSOURCE_ID", "LOADERSOURCE_TYPE");

                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err)=>{
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                })
        });
        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_LoaderSource.cif.LOADERSOURCE_TYPE + "(" + self.WB_LoaderSource.cif.LOADERSOURCE_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject)=>{
            //Does no validations yet
            self.ValidateOptions = options || {};

            if(self.dbContext == null) {
                let sError = "Unable to determine dbContext to Validate Loader Source";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            LoaderSourceBOValidation.ValidateFields(self)
                .then((ignore)=>{
                    if(self.ValidateMessageProvider.countMessages(MessageType.Error) > 0){
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                }).catch((err)=>{
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);
            });
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbLoaderSource = <IwbLoaderSource>self.drWB_LoaderSource;
        this.SaveProgressProvider.start("Saving " + wbLoaderSource.LOADERSOURCE_TYPE + "(" + wbLoaderSource.LOADERSOURCE_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject)=>{
            //Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Loader Source.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }
            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if(bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            let bDone = false;
            self.Validate({})
                .then((ignore)=>{
                    if(!self.bIsValidated) {
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return;
                    }

                    let tasks: Promise<any> [] = [];
                    tasks.push(saveFunction(esWB_Loader_Source, self.drWB_LoaderSource, {}));
                    return Promise.all(tasks);
                }).then((ignored)=>{
                    if(bDone) return;

                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(()=> {
                            self._bIsSaved = true;
                        })
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                }).catch((err)=>{
                    self.SaveProgressProvider.exception(err);
                    resolve(self)
                })
        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbLoaderSource = <IwbLoaderSource>self.drWB_LoaderSource;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbLoaderSource.LOADERSOURCE_TYPE + "(" + wbLoaderSource.LOADERSOURCE_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject)=>{
            // 1. Validate if we can delete the Loader Source

            // 2. If Valid, mark all the records for deletion.
            self.drWB_LoaderSource.markDeleted()
            if (bSaveNow){
                self
                    .SaveUsing(false)
                    .then(ignore => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch(err => {
                        this.DeleteProgressProvider.exception(err);
                        reject(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        })
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const loader_source_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, loader_source_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void>{
        return new Promise<void>((resolve, reject)=>{
            recordResolver.findAllWithRelationships(esWB_Loader_Source, ids)
                .then((result)=>{
                    resolve();
                })
                .catch((error)=>{
                    reject(error);
                })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( LoaderSourceBO , "LoaderSourceBO", "Loader Source")